import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import styled from 'styled-components/macro';

import { Modal } from '../SubComponents/Modals';
import { Label, Text } from '../SubComponents/Text';
import { ModalButton, CloseButton } from '../SubComponents/Buttons';
import { translateErrorShort } from '../Utils/locale';
import { ReactComponent as Close } from '../Assets/Icons/close.svg';
import { Card } from '../SubComponents/Inputs';
import CustomerInfo from './CustomerInfo';
import { checkFeatureFlag } from '../Utils/utils';
import useLocalize from '../Hooks/useLocalize';
import useFormatPrice from '../Hooks/use-format-price';
import useLocale from '../Hooks/use-locale';
import localizations from '../localizations';

import {
  PaymentMethod,
  PaymentRequest,
  PaymentRequestOptions,
  StripeCardElementChangeEvent,
  StripePaymentRequestButtonElementClickEvent,
} from '@stripe/stripe-js';
import { CustomerData } from '../Pages/Booking/data-components/checkout-modal/checkout-modal';
import Voucher, { VoucherProps } from './Voucher';

const PaymentRequestButton = styled(PaymentRequestButtonElement)<{ isLoading: boolean }>`
  opacity: ${(props) => (props.isLoading ? 0.35 : 1)};
`;

const PaymentSplitter = styled.div`
  margin: 16px auto;
`;

const Line = styled.span`
  display: inline-block;
  width: 50px;
  height: 1px;
  background-color: #b6bbc4;
  margin: 0 16px;
  vertical-align: super;
`;

const PaymentSplitterLabel = styled(Label)`
  color: #959dad;
`;

const Error = ({ errorMessage, mb }: { errorMessage: string; mb?: string | number }) => {
  const localize = useLocalize();
  return (
    <Label error mb={mb ? mb : '3'}>
      {localize(`error.${errorMessage}`, errorMessage)}
    </Label>
  );
};

export interface PaymentModalProps {
  customer: CustomerData;
  recipient?: {
    name?: string;
  };
  showBirthDate?: boolean;
  handleNegativeBalance: (method?: PaymentMethod) => unknown;
  handleZeroBalance: React.MouseEventHandler<HTMLButtonElement>;
  handlePositiveBalance: React.MouseEventHandler<HTMLButtonElement>;
  onPaymentMethodComplete?: (paymentType: string) => void;
  isLoading: boolean;
  error?: string;
  voucher: VoucherProps;
  refundable?: boolean;
  paymentRequestObject?: PaymentRequestOptions | null;
  shop?: {
    featureFlags: { featureName: string; enabled: boolean }[];
  } | null;
  prices: {
    balance?: number;
    toBePaid?: number;
  };
  setSummaryOpen: (open: boolean) => void;
  setShowPaymentModal: (show: boolean) => void;
  setError: (error: string | null) => void;
}

const PaymentModal = ({
  customer,
  recipient,
  showBirthDate,
  handleZeroBalance,
  handlePositiveBalance,
  handleNegativeBalance,
  onPaymentMethodComplete,
  isLoading,
  error,
  voucher,
  refundable = true,
  paymentRequestObject = null,
  shop,
  prices,
  setSummaryOpen,
  setShowPaymentModal,
  setError,
}: PaymentModalProps) => {
  const { languageAsEnum } = useLocale();
  const localization = localizations[languageAsEnum];
  const localize = useLocalize();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const stripe = useStripe();
  const formatPrice = useFormatPrice();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);

  const handleCardChanges = (event: StripeCardElementChangeEvent) => {
    if (event.complete && onPaymentMethodComplete) onPaymentMethodComplete('card');

    event.error
      ? setError(translateErrorShort(event.error.message, localization))
      : setError(null);
  };

  useEffect(() => {
    if (stripe && paymentRequestObject && !paymentRequest) {
      const pr = stripe.paymentRequest(paymentRequestObject);

      // Check the availability of the Payment Request API.
      pr.canMakePayment().then((result) => {
        if (result) setPaymentRequest(pr);

        // Listen for 'paymentmethod' event
        // This get triggered when the customer has confirm the payment
        pr.on('paymentmethod', ({ paymentMethod, complete }) => {
          handleNegativeBalance(paymentMethod);

          // Dismis the payment sheet
          complete('success');
        });
      });
    }

    // Clean up the "on payment method" event listener
    return () => {
      if (paymentRequest) {
        paymentRequest.off('paymentmethod');
        setPaymentRequest(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, paymentRequestObject, paymentRequest]);

  const renderPaymentRequest =
    paymentRequest && checkFeatureFlag(shop ?? {}, 'MOBILE_PAYMENTS');

  const handlePaymentRequestClick = (
    event: StripePaymentRequestButtonElementClickEvent
  ) => {
    if (isLoading) event.preventDefault();
  };

  return (
    <Modal style={{ marginBottom: isMobile ? '200px' : '32px' }}>
      {isMobile && (
        <CloseButton
          icon={Close}
          altText="Close payment modal"
          onClick={() => {
            setSummaryOpen(false);
            setShowPaymentModal(false);
          }}
        />
      )}

      <CustomerInfo
        customer={customer}
        recipient={recipient}
        showBirthDate={showBirthDate}
      />

      {prices.balance && prices.balance < 0 && (
        <>
          {renderPaymentRequest && (
            <>
              <PaymentRequestButton
                onClick={handlePaymentRequestClick}
                isLoading={isLoading}
                options={{
                  paymentRequest,
                  style: {
                    paymentRequestButton: {
                      height: '35px',
                    },
                  },
                }}
              />

              <PaymentSplitter>
                <Line />
                <PaymentSplitterLabel>{localize('product.OR')}</PaymentSplitterLabel>
                <Line />
              </PaymentSplitter>
            </>
          )}

          <Card onChange={handleCardChanges} isMobile={isMobile} isLoading={isLoading} />
          {error && <Error errorMessage={error} mb="4" />}

          <Voucher {...voucher} />

          <Label textAlign="center">
            {localize('product.toBePaid')}:{' '}
            {prices.toBePaid ? formatPrice(prices.toBePaid) : '-'}
          </Label>

          <ModalButton isLoading={isLoading} onClick={() => handleNegativeBalance()}>
            {localize('product.confirmPayment')}
          </ModalButton>
        </>
      )}

      {prices.balance === 0 && (
        <>
          <Text mb="3">{localize('product.neutralBalance')}</Text>
          {error && <Error errorMessage={error} />}

          <ModalButton isLoading={isLoading} onClick={handleZeroBalance}>
            {localize('product.confirmBooking')}
          </ModalButton>
        </>
      )}

      {prices.balance && prices.balance > 0 && (
        <>
          <Text mb="2">
            {localize('product.positiveBalance1')}{' '}
            {refundable
              ? localize('product.positiveBalance2Refund')
              : localize('product.positiveBalance2VoucherRefund')}
          </Text>

          {error && <Error errorMessage={error} />}

          <ModalButton isLoading={isLoading} onClick={handlePositiveBalance}>
            {localize('product.confirmBooking')}
          </ModalButton>
        </>
      )}
    </Modal>
  );
};

export default PaymentModal;
